import React, { useState, useEffect, useCallback } from 'react';
import { Wallet, DollarSign, Calendar, TrendingUp, TrendingDown, AlertCircle, Loader2, Tag } from 'lucide-react';
import { auth } from '../config/firebase';
import { financeiroService } from '../services/api';
import PaywallCard from '../components/PaywallCard';

const formatarPreco = (valor) => {
  if (valor === null || valor === undefined) return 'R$ 0,00';
  return Number(valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const hojeInput = new Date().toLocaleDateString('en-CA');
const primeiroDiaMesInput = (() => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toLocaleDateString('en-CA');
})();

const FinanceiroPage = ({ temAcesso }) => {
  const isPremium = temAcesso('financeiro');
  const [dataInicio, setDataInicio] = useState(primeiroDiaMesInput);
  const [dataFim, setDataFim] = useState(hojeInput);
  const [resumo, setResumo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');

  const intervaloValido = dataInicio && dataFim && dataInicio <= dataFim;

  const carregarResumo = useCallback(async () => {
    if (!isPremium || !intervaloValido) return;
    setLoading(true);
    setErro('');
    try {
      const user = auth.currentUser;
      if (user) {
        const res = await financeiroService.obterResumo(user.uid, dataInicio, dataFim);
        setResumo(res.data);
      }
    } catch (error) {
      setErro(error.response?.data?.erro || 'Erro ao carregar o resumo financeiro.');
    } finally {
      setLoading(false);
    }
  }, [dataInicio, dataFim, isPremium, intervaloValido]);

  useEffect(() => { carregarResumo(); }, [carregarResumo]);

  if (!isPremium) {
    return (
      <PaywallCard
        titulo="Controle Financeiro"
        descricao="Acompanhe a receita do seu negócio: total por período, ticket médio e receita por serviço."
        plano="Comum"
      />
    );
  }

  const stats = resumo ? [
    { label: 'Receita Total', value: formatarPreco(resumo.receitaTotal), icon: <DollarSign size={22} />, textColor: 'text-emerald-600', bgColor: 'bg-emerald-500/15' },
    { label: 'Atendimentos Concluídos', value: String(resumo.totalAtendimentos).padStart(2, '0'), icon: <Calendar size={22} />, textColor: 'text-[#1883FF]', bgColor: 'bg-[#1883FF]/15' },
    { label: 'Ticket Médio', value: formatarPreco(resumo.ticketMedio), icon: <Wallet size={22} />, textColor: 'text-[#6D28D9]', bgColor: 'bg-[#6D28D9]/15' },
  ] : [];

  const maiorReceitaServico = resumo && resumo.receitaPorServico.length > 0
    ? Math.max(...resumo.receitaPorServico.map(s => s.total))
    : 0;

  return (
    <div className="w-full max-w-5xl mx-auto p-2 sm:p-4 flex flex-col gap-8 animate-fade-in-up">

      {/* FILTRO DE INTERVALO DE DATAS */}
      <div className="flex flex-col items-center gap-2">
        <div className="flex flex-wrap items-center justify-center gap-3 bg-slate-100 p-3 rounded-2xl shadow-inner">
          <div className="flex items-center gap-2">
            <label className="text-[10px] font-black uppercase text-slate-500 tracking-wide">De</label>
            <input
              type="date"
              value={dataInicio}
              max={dataFim}
              onChange={e => setDataInicio(e.target.value)}
              className="bg-white border border-purple-50 py-2 px-3 rounded-xl text-xs font-bold text-slate-800 outline-none transition-all focus:border-[#6D28D9] focus:ring-4 focus:ring-[#6D28D9]/10"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-[10px] font-black uppercase text-slate-500 tracking-wide">Até</label>
            <input
              type="date"
              value={dataFim}
              min={dataInicio}
              max={hojeInput}
              onChange={e => setDataFim(e.target.value)}
              className="bg-white border border-purple-50 py-2 px-3 rounded-xl text-xs font-bold text-slate-800 outline-none transition-all focus:border-[#6D28D9] focus:ring-4 focus:ring-[#6D28D9]/10"
            />
          </div>
        </div>
        {!intervaloValido && (
          <p className="text-[11px] text-red-600 font-bold flex items-center gap-1.5 m-0"><AlertCircle size={12} /> A data de início não pode ser depois da data de fim.</p>
        )}
        {erro && (
          <p className="text-[11px] text-red-600 font-bold flex items-center gap-1.5 m-0"><AlertCircle size={12} /> {erro}</p>
        )}
      </div>

      {loading || !resumo ? (
        <div className="flex flex-col items-center justify-center h-[40vh] w-full gap-4">
          <Loader2 className="animate-spin text-[#1883FF]" size={40} />
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Calculando sua receita...</p>
        </div>
      ) : (
        <>
          {/* VARIAÇÃO VS PERÍODO ANTERIOR */}
          {resumo.variacaoPercentual !== null && (
            <div className="flex justify-center">
              <span className={`flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wide px-3 py-1.5 rounded-lg ${resumo.variacaoPercentual >= 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>
                {resumo.variacaoPercentual >= 0 ? <TrendingUp size={13} /> : <TrendingDown size={13} />}
                {resumo.variacaoPercentual >= 0 ? '+' : ''}{resumo.variacaoPercentual.toFixed(1)}% vs período anterior de mesma duração
              </span>
            </div>
          )}

          {/* CARDS */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {stats.map((item, idx) => (
              <div key={idx} className="bg-[#F8F7FF] p-6 rounded-[2rem] border border-purple-50 flex items-center gap-5 transition-all duration-300 hover:-translate-y-1 hover:bg-white hover:shadow-[0_20px_40px_rgba(109,40,217,0.05)] hover:border-[#6D28D9]/20">
                <div className={`w-14 h-14 rounded-[18px] flex items-center justify-center shrink-0 ${item.bgColor} ${item.textColor}`}>{item.icon}</div>
                <div className="min-w-0">
                  <p className="text-[10px] font-black text-[#94a3b8] uppercase tracking-[0.15em] mb-1 break-words">{item.label}</p>
                  <p className="text-xl font-black tracking-tight m-0 text-[#1e293b] break-words">{item.value}</p>
                </div>
              </div>
            ))}
          </div>

          {/* RECEITA POR SERVIÇO */}
          <div className="bg-white p-6 sm:p-8 rounded-[2.5rem] border border-purple-50">
            <h3 className="text-sm font-black text-[#1e293b] uppercase tracking-wider mb-6 flex items-center gap-2.5 italic">
              <Tag size={18} className="text-[#6D28D9]" /> Receita por Serviço
            </h3>
            {resumo.receitaPorServico.length === 0 ? (
              <p className="text-sm font-semibold text-slate-400 italic py-2">Nenhum atendimento concluído nesse período.</p>
            ) : (
              <div className="flex flex-col gap-4">
                {resumo.receitaPorServico.map((s, idx) => (
                  <div key={idx} className="flex flex-col gap-1.5">
                    <div className="flex justify-between items-center text-xs">
                      <span className="font-bold text-slate-700">{s.nome}</span>
                      <span className="font-black text-slate-900">{formatarPreco(s.total)}</span>
                    </div>
                    <div className="w-full h-2.5 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-[#1883FF] to-[#6D28D9] rounded-full"
                        style={{ width: maiorReceitaServico > 0 ? `${(s.total / maiorReceitaServico) * 100}%` : '0%' }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* AVISO: ATENDIMENTOS SEM VALOR DEFINIDO */}
          {resumo.atendimentosSemValor > 0 && (
            <div className="bg-amber-50 border border-amber-100 rounded-2xl px-5 py-4 flex items-center gap-3 text-amber-800">
              <AlertCircle size={18} className="shrink-0" />
              <p className="text-xs font-semibold m-0">
                {resumo.atendimentosSemValor} atendimento{resumo.atendimentosSemValor > 1 ? 's' : ''} concluído{resumo.atendimentosSemValor > 1 ? 's' : ''} sem valor definido (criado{resumo.atendimentosSemValor > 1 ? 's' : ''} sem um serviço associado) não entra{resumo.atendimentosSemValor > 1 ? 'm' : ''} na receita total.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default FinanceiroPage;
